import { formatCurrency } from '@if/core/utils'
import { FieldValue, Help, RadioTableColumn, StrongValue } from '@if/mui'
import { Chip, Stack } from '@mui/material'
import { formatComputed } from '@src/data/worksheet-selectors'
import { TermAndPlanOption } from '@src/types/TermPlanOptionDto'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const getInvalidTermReasonKey = (
  reason?: string,
): 'worksheet.errors.pmtTooHigh' | 'worksheet.errors.aprTooHighTerm' | undefined => {
  if (reason === 'pmtTooHigh') return 'worksheet.errors.pmtTooHigh'
  if (reason === 'aprTooHigh') return 'worksheet.errors.aprTooHighTerm'
  return undefined
}

/** Whether an option can be picked — a string is "no, and here is why". */
export function useTermAndPlanDisabledReason(): (row: TermAndPlanOption) => boolean | string {
  const { t } = useTranslation()

  return useCallback(
    (row: TermAndPlanOption) => {
      if (row.disabled !== true) return false
      const reasonKey = getInvalidTermReasonKey(row.reason)
      return reasonKey ? t(reasonKey) : true
    },
    [t],
  )
}

export function useTermAndPlanColumns(): RadioTableColumn<TermAndPlanOption>[] {
  const { t } = useTranslation()

  return useMemo<RadioTableColumn<TermAndPlanOption>[]>(
    () => [
      {
        key: 'term',
        header: t('worksheet.loanTerm'),
        render: (row) => <FieldValue>{t('worksheet.termMonths', { count: row.termMonths })}</FieldValue>,
      },
      {
        key: 'plan',
        header: t('worksheet.paymentPlan'),
        // A promotional plan is a badge; the regular plan is plain text.
        render: (row) =>
          row.planLabel ? (
            <Chip size="small" color="info" label={row.planLabel} />
          ) : (
            <FieldValue>{t('worksheet.regularPlan')}</FieldValue>
          ),
      },
      {
        key: 'payment',
        header: t('worksheet.paymentAmount'),
        align: 'right',
        showsReason: true,
        render: (row) => <StrongValue>{formatComputed(row.monthlyPayment, formatCurrency)}</StrongValue>,
      },
      {
        key: 'rate',
        header: t('worksheet.interestRate'),
        align: 'right',
        render: (row) => (
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
            {row.promoRate === 0 ? (
              <StrongValue sx={{ color: 'primary.main' }}>{`${row.promoRate}%`}</StrongValue>
            ) : (
              <FieldValue>{`${row.promoRate}%`}</FieldValue>
            )}
            {row.promoDurationMonths != null && (
              <Help
                title={t('worksheet.promoRateDetail', {
                  promoRate: row.promoRate,
                  regularRate: row.regularRate,
                  count: row.promoDurationMonths,
                })}
              />
            )}
          </Stack>
        ),
      },
    ],
    [t],
  )
}
